import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
const Step1 = lazy(() =>
  import("@/components/menu-management/drink-manage/add-drink/step1")
);
const Step2 = lazy(() =>
  import("@/components/menu-management/drink-manage/add-drink/step2")
);
const Step3 = lazy(() =>
  import("@/components/menu-management/drink-manage/add-drink/step3")
);
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewDrink,
  updateExistingDrink,
} from "@/redux/slice/menu-manage-slice/drink-slice";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const drink_id = 1;

function CreateDrinks() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const location = useLocation();
  const item = location.state;
  const { type } = useParams();

  const fetchOnly = useMemo(
    () => ["ingredients", "categories", "addOns", "allergens", "brands"],
    []
  );

  const { groupLoading } = useFetchAllMenuData(user.restaurant_id, drink_id, {
    fetchOnly,
  });

  const { createLoading, updateLoading, error } = useSelector(
    (state) => state.menuManage.drink
  );

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Drink",
      user,
      createAction: createNewDrink,
      updateAction: updateExistingDrink,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/drinks",
    });

  const defaultValues = useMemo(
    () => ({
      type: "create",
      item_no: "",
      name: "",
      description: "",
      category_id: null,
      brand_id: null,
      vat: 0,
      alcohol_percentage: "",
      status: "active",
      file_manager_id: undefined,
      ingredient_ids: [],
      add_on_ids: [],
      allergen_ids: [],
      variant_ids: [],
    }),
    []
  );

  const form = useForm({
    resolver: zodResolver(createDrinkSchema),
    mode: "onBlur",
    defaultValues,
  });

  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/drinks");
      return;
    }
    form.reset({
      type: "update",
      item_no: item.item_no || "",
      name: item.name || "",
      description: item.description || "",
      category_id: item.category_id || null,
      brand_id: item.brand_id || null,
      vat: item.vat ?? 0,
      alcohol_percentage: item.alcohol_percentage ?? "",
      status: item.status || "active",
      file_manager_id: item.file_manager_id || undefined,
      ingredient_ids: item.ingredients?.map((ing) => ing.id) || [],
      add_on_ids: item.add_ons?.map((a) => a.id) || [],
      allergen_ids: item.allergens?.map((a) => a.id) || [],
      variant_ids: item.variants?.map((v) => v.id) || [],
    });

    if (item.file_manager_id && item.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    dispatch(setItemVariants(item.variants));
  }, [type, item, form]);

  if (groupLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${type == "update" ? "Update Drink" : "Add New Drink"}`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: "Drinks", path: "/backoffice/menu/drinks" },
        {
          name: `${type == "update" ? "Update Drink" : "Add New Drink"}`,
          path: `/backoffice/menu/drinks/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {/* {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )} */}
        <Form {...form}>
          <form>
            <Suspense fallback={<div>Loading...</div>}>
              {step === 0 && <Step1 drink_id={drink_id} form={form} />}
              {step === 1 && (
                <Step2
                  item_id={entityId || item?.id}
                  drink_id={drink_id}
                  form={form}
                />
              )}
              {step === 2 && (
                <Step3 files={files} setFiles={setFiles} form={form} />
              )}
            </Suspense>

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/drinks"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateDrinks;

export const createDrinkSchema = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  category_id: z.preprocess((val) => {
    if (val === "" || val === undefined || val === null) return null;
    return Number(val);
  }, z.number().nullable().optional()),

  brand_id: z.preprocess(
    (val) => {
      if (val === undefined || val === null || val === "") return undefined;
      return Number(val);
    },
    z
      .number({
        invalid_type_error: "Brand must be a number",
      })
      .optional()
  ),
  variant_ids: z
    .array(z.number(), {
      required_error: "Please select at least one variant",
    })
    .optional(),
  ingredient_ids: z.array(z.number()).optional(),
  add_on_ids: z.array(z.number()).optional(),
  allergen_ids: z.array(z.number()).optional(),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  alcohol_percentage: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "Alcohol percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "Alcohol percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "Alcohol percentage cannot exceed 100",
    })
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
